import { Product } from '../../domain/types';
import { COLLECTIONS } from '../../domain/collections';
import { db } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';
import { cacheManager, CACHE_TTL } from '../../utils/cache';
import { PAGINATION, REPOSITORIES, DISPLAY_LIMITS, STOCK } from '../../domain/constants';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  DocumentSnapshot,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { BaseRepository, BaseEntity } from '../../domain/services/BaseRepository';

interface ProductEntity extends Product, BaseEntity {}

export class ProductQueries extends BaseRepository<ProductEntity> {
  protected collectionName = COLLECTIONS.PRODUCTS;

  protected toEntity(doc: QueryDocumentSnapshot): ProductEntity {
    const data = doc.data() as Product;
    return {
      ...data,
      id: doc.id,
      originalPrice: data.originalPrice || data.price,
      stock: data.stock ?? 0,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    };
  }

  async getFeaturedProducts(): Promise<Product[]> {
    try {
      const q = query(collection(db, this.collectionName), where("isFeatured", "==", true));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
    } catch (error) {
      logger.error('[ProductQueries] Error fetching featured products', error);
      return [];
    }
  }

  async getAllProducts(lastVisible: DocumentSnapshot | null = null, pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<{ products: Product[], lastDoc: DocumentSnapshot | null }> {
    try {
      const constraints: QueryConstraint[] = [
        orderBy("createdAt", "desc"),
        limit(pageSize)
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => ({ ...(doc.data() as object), id: doc.id } as Product));
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

      return { products, lastDoc };
    } catch (error) {
      logger.error('[ProductQueries] Error fetching all products', error);
      try {
        const q = query(collection(db, this.collectionName), limit(pageSize));
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => ({ ...(doc.data() as object), id: doc.id } as Product));
        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
        return { products, lastDoc };
      } catch (fallbackError) {
        logger.error('[ProductQueries] Fallback also failed', fallbackError);
        return { products: [], lastDoc: null };
      }
    }
  }

  async getProductsByCategory(category: string, lastVisible: DocumentSnapshot | null = null, pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<{ products: Product[], lastDoc: DocumentSnapshot | null }> {
    try {
      if (!category) return { products: [], lastDoc: null };
      const constraints: QueryConstraint[] = [
        where("category", "==", category),
        limit(pageSize)
      ];
      if (lastVisible) constraints.push(startAfter(lastVisible));

      const q = query(collection(db, this.collectionName), ...constraints);
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

      return { products, lastDoc };
    } catch (error) {
      logger.error(`[ProductQueries] Error fetching products for category ${category}`, error);
      return { products: [], lastDoc: null };
    }
  }

  async getProductById(id: string): Promise<Product | undefined> {
    try {
      const cached = cacheManager.get<Product>('product', id);
      if (cached) {
        logger.debug(`[ProductRepository] Cache hit for product ${id}`);
        return cached;
      }

      const product = await this.findById(id);
      if (product) {
        cacheManager.set('product', id, product, CACHE_TTL.PRODUCTS);
      }
      return product;
    } catch (error) {
      logger.error('[ProductQueries] Error fetching product', error);
      return undefined;
    }
  }

  async getProductsByIds(ids: string[]): Promise<Product[]> {
    try {
      if (!ids || ids.length === 0) return [];

      const uniqueIds = Array.from(new Set(ids.filter(id => id && typeof id === 'string')));
      if (uniqueIds.length === 0) return [];

      const cached: Product[] = [];
      const toFetch: string[] = [];

      for (const id of uniqueIds) {
        const product = cacheManager.get<Product>('product', id);
        if (product) {
          cached.push(product);
        } else {
          toFetch.push(id);
        }
      }

      if (toFetch.length === 0) return cached;

      let fetched: Product[] = [];

      if (toFetch.length <= REPOSITORIES.FIRESTORE_IN_QUERY_LIMIT) {
        const q = query(
          collection(db, this.collectionName),
          where('id', 'in', toFetch)
        );
        const snapshot = await getDocs(q);
        fetched = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
      } else {
        const batchSize = REPOSITORIES.FETCH_BATCH_SIZE;
        const batches: Product[][] = [];

        for (let i = 0; i < toFetch.length; i += batchSize) {
          const batchIds = toFetch.slice(i, i + batchSize);
          const q = query(
            collection(db, this.collectionName),
            where('id', 'in', batchIds)
          );
          const snapshot = await getDocs(q);
          const batchProducts = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
          batches.push(batchProducts);
        }

        fetched = batches.flat();
      }

      fetched.forEach(product => {
        cacheManager.set('product', product.id, product, CACHE_TTL.PRODUCTS);
      });

      return [...cached, ...fetched];
    } catch (error) {
      logger.error('[ProductQueries] Error fetching products by IDs', error);
      return [];
    }
  }

  async getProductsByShopIds(shopIds: string[], lastVisible: DocumentSnapshot | null = null, pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<{ products: Product[], lastDoc: DocumentSnapshot | null }> {
    try {
      if (!shopIds || shopIds.length === 0) return { products: [], lastDoc: null };

      const constraints: QueryConstraint[] = [
        where("shopId", "in", shopIds.slice(0, REPOSITORIES.FIRESTORE_IN_QUERY_LIMIT)),
        orderBy("createdAt", "desc"),
        limit(pageSize)
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

      return { products, lastDoc };
    } catch (error) {
      logger.error('[ProductQueries] Error fetching products by shop IDs', error);
      return { products: [], lastDoc: null };
    }
  }

  async getRecommendedProducts(excludeIds: string[]): Promise<Product[]> {
    try {
      const q = query(collection(db, this.collectionName), limit(REPOSITORIES.DEFAULT_LIMIT));
      const snapshot = await getDocs(q);
      const products = snapshot.docs
        .map(doc => ({ ...doc.data(), id: doc.id } as Product))
        .filter(p => !excludeIds.includes(p.id));

      return products.sort(() => 0.5 - Math.random()).slice(0, DISPLAY_LIMITS.RECOMMENDED_PRODUCTS);
    } catch (error) {
      logger.error('[ProductQueries] Error fetching recommended products', error);
      return [];
    }
  }

  async getUsedProducts(pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<{ products: Product[], lastDoc: DocumentSnapshot | null }> {
    try {
      const validConditions = ['used_like_new', 'good', 'fair', 'poor'];

      const q = query(
        collection(db, this.collectionName),
        where('status', '==', 'active'),
        where('approvalStatus', '==', 'active'),
        where('condition', 'in', validConditions),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      const snapshot = await getDocs(q);
      const usedProducts = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));

      return { products: usedProducts, lastDoc: snapshot.docs[snapshot.docs.length - 1] || null };
    } catch (error) {
      logger.error('[ProductQueries] Error in getUsedProducts', error);

      try {
        const q = query(collection(db, this.collectionName), limit(pageSize * 3));
        const snapshot = await getDocs(q);
        const usedProducts = snapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id } as Product))
          .filter(p =>
            p.status === 'active' &&
            p.approvalStatus === 'active' &&
            p.condition && p.condition !== 'new'
          )
          .slice(0, pageSize);

        return { products: usedProducts, lastDoc: snapshot.docs[snapshot.docs.length - 1] || null };
      } catch (fallbackError) {
        logger.error('[ProductQueries] Fallback also failed', fallbackError);
        return { products: [], lastDoc: null };
      }
    }
  }

  async getPendingProducts(): Promise<Product[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("approvalStatus", "==", "pending_review"),
        orderBy("createdAt", "desc"),
        limit(PAGINATION.FILTERED_RESULTS_LIMIT)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
    } catch (error) {
      logger.error('[ProductQueries] Error fetching pending products', error);
      try {
        const q = query(collection(db, this.collectionName), limit(PAGINATION.BULK_QUERY_LIMIT));
        const snapshot = await getDocs(q);
        return snapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id } as Product))
          .filter(p => p.approvalStatus === 'pending_review' || p.status === 'pending_review');
      } catch (fallbackError) {
        logger.error('[ProductQueries] Fallback also failed for pending products', fallbackError);
        return [];
      }
    }
  }

  async getActiveProducts(limitCount: number = PAGINATION.FILTERED_RESULTS_LIMIT): Promise<Product[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("status", "==", "active"),
        where("approvalStatus", "==", "active"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
    } catch (error) {
      logger.error('[ProductQueries] Error fetching active products with composite index', error);

      try {
        const q = query(
          collection(db, this.collectionName),
          where("status", "==", "active"),
          orderBy("createdAt", "desc"),
          limit(limitCount * 2)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id } as Product))
          .filter(p => p.approvalStatus === 'active' && p.shopId && p.shopId.trim() !== '');
      } catch (fallbackError) {
        logger.error('[ProductQueries] Fallback 1 failed, trying fallback 2', fallbackError);

        try {
          const q = query(
            collection(db, this.collectionName),
            limit(limitCount * 3)
          );
          const snapshot = await getDocs(q);
          return snapshot.docs
            .map(doc => ({ ...doc.data(), id: doc.id } as Product))
            .filter(p =>
              p.status === 'active' &&
              p.approvalStatus === 'active' &&
              p.shopId &&
              p.shopId.trim() !== ''
            )
            .slice(0, limitCount);
        } catch (fallback2Error) {
          logger.error('[ProductQueries] All fallbacks failed for active products', fallback2Error);
          return [];
        }
      }
    }
  }

  async getLowStockProducts(threshold: number = STOCK.LOW_STOCK_THRESHOLD): Promise<number> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("stock", "<=", threshold),
        where("stock", ">", 0)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      logger.error('[ProductQueries] Error fetching low stock count', error);
      return 0;
    }
  }

  async getProductsByShop(shopId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("shopId", "==", shopId),
        limit(PAGINATION.FILTERED_RESULTS_LIMIT)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
    } catch (error) {
      logger.error('[ProductQueries] Error fetching products by shop', error);
      return [];
    }
  }

  async getProductsByPartner(partnerId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("partnerId", "==", partnerId),
        orderBy("createdAt", "desc"),
        limit(PAGINATION.BULK_QUERY_LIMIT)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Product));
    } catch (error) {
      logger.error('[ProductQueries] Error fetching products by partner', error);
      return [];
    }
  }
}

export const productQueries = new ProductQueries();
